import * as THREE from 'three';
import { ballRadius } from '../sim/world.js';

/**
 * 統合3Dショットウィジェット — Spike A/B（描画核＋対話の使い捨て検証）。
 *
 * 設計: `knowledge/architecture/unified-shot-widget.md`（案A 専用ミニ3Dシーン）。
 * 目的: 手玉＋キュー先のクローズアップを「背景透明・GPL非依存・自前メッシュ」で、
 *       既存 WebGLRenderer の 2 パス目（setScissor）として画面右下（撞点UI領域）へ描き、
 *       ドラッグで撞点（上半分）・仰角（下半分）を立体的に動かせるかを検証する。
 *
 * 重要(R-2): 本モジュールは GPL（render/vendor/**）を一切 import しない自前描画。
 * キュー先は表示専用の簡易メッシュ（物理用 CueMesh の複製はしない）。
 *
 * 座標系: z-up を踏襲。撞かれる面＝カメラ向き(+Y)・右=+X・上=+Z。
 *   撞点 (ox,oy)∈[-0.5,0.5] → 面上の接触点 (ox·R, √(R²−…), oy·R)。
 *   キュー先は接触点から外側 cosθ·horiz + sinθ·up（θ=仰角）方向へ伸ばす。
 */
export interface CueSpinWidget {
  /** メイン描画の直後に呼ぶ。画面右下の正方形領域へ 2 パス目として描画する。 */
  render(renderer: THREE.WebGLRenderer, cssW: number, cssH: number): void;
  /** 撞点（赤スポット＋キュー接触点）を更新。 */
  setContact(ox: number, oy: number): void;
  /** 仰角[rad]を更新（キューの立ち上がり）。 */
  setElevation(rad: number): void;
  /** 破棄（DOM オーバーレイ・ジオメトリ/マテリアルを解放）。 */
  dispose(): void;
}

const R = ballRadius;
const FACE_AXIS = new THREE.Vector3(0, 1, 0); // シリンダのローカル軸(+Y)を外向きへ回す基準
const MAX_ELEV = Math.PI / 3; // 仰角上限 60°
const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

export const createCueSpinWidget = (environment: THREE.Texture | null): CueSpinWidget => {
  const scene = new THREE.Scene();
  scene.background = null; // 背景透明（描画時に色クリアせず深度のみクリアして盤面の上に重ねる）
  if (environment) scene.environment = environment;

  // カメラ: 3/4 アングル（正面やや横＋上）で立体感を出す。aspect は描画時 1 固定。
  const camera = new THREE.PerspectiveCamera(33, 1, 0.01, 100);
  camera.up.set(0, 0, 1); // z-up
  camera.position.set(1.7 * R, 5.4 * R, 1.6 * R);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld(true);
  // カメラ基底（固定）。撞点入力・キュー配置をすべてこの「画面の右/上/手前」軸で扱い、
  // クリック位置と描画を一致させ左右上下を対称にする（3/4 カメラの回転を吸収）。
  const camRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
  const camUp = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
  const camToward = camera.position.clone().normalize(); // ボール中心(原点)→カメラ方向（手前）
  // 仰角はテーブル水平面(xy)からの起こし角で扱う。そのための水平基底（z を落とした手前/右）。
  const Z_UP = new THREE.Vector3(0, 0, 1);
  const camFlat = new THREE.Vector3(camera.position.x, camera.position.y, 0).normalize(); // 水平・プレイヤー側
  const camFlatRight = new THREE.Vector3().crossVectors(Z_UP, camFlat).normalize(); // 水平・画面右側

  // 3 灯構成で球の立体感（明暗グラデーション＋縁の立ち上がり）を出す。
  // 白球は陰影が washout しやすいので、環境光・補助光は弱めにして「影側」を残す。
  const ambient = new THREE.AmbientLight(0xffffff, 0.22);
  scene.add(ambient);
  const key = new THREE.DirectionalLight(0xffffff, 1.95); // 主光源（右上手前）= 明確な terminator
  key.position.set(1.8, 1.6, 2.6);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xdfe6ff, 0.2); // 補助（左・寒色）= 影側を黒潰れさせない程度に弱く
  fill.position.set(-2.4, 0.8, 0.9);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xffffff, 0.5); // 縁光（後方下）= 球の輪郭を背景から分離
  rim.position.set(-1.0, -2.2, 1.6);
  scene.add(rim);

  // 手球（balls.ts の手球と同質感・高分割で縁を滑らかに）。
  const ballGeo = new THREE.IcosahedronGeometry(R, 5);
  const ballMat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(0xf3f1e7),
    roughness: 0.16,
    metalness: 0,
    clearcoat: 0.45,
    clearcoatRoughness: 0.06,
    envMapIntensity: 0.55, // 反射を抑えめにして陰影（マット感）で立体を出す（白飛び回避）
  });
  const ball = new THREE.Mesh(ballGeo, ballMat);
  scene.add(ball);

  // 練習球の赤ドット（6 軸方向）。balls.ts の addCueDots と同仕様で、実手玉と見た目を揃える。
  // ball の子にするので ball.quaternion を実手玉に合わせれば斑点位置も一致する（1-B で連動）。
  const dotGeo = new THREE.CircleGeometry(R * 0.17, 16);
  const dotMat = new THREE.MeshStandardMaterial({ color: 0xcc1111, roughness: 0.4 });
  {
    const dotDirs = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, -1),
    ];
    const zAxis = new THREE.Vector3(0, 0, 1);
    for (const dir of dotDirs) {
      const dot = new THREE.Mesh(dotGeo, dotMat);
      dot.position.copy(dir).multiplyScalar(R * 1.002);
      dot.quaternion.setFromUnitVectors(zAxis, dir); // 面を外向きに
      ball.add(dot);
    }
  }

  // 接地影: 球の真下（テーブル面 z=-R）に半透明の放射状グラデを敷き「台に置いた球」感を出す。
  // 背景は透明合成なので、この円盤が後ろの実テーブルをわずかに暗くして影に見える。
  const makeGroundShadow = (): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    const g2d = c.getContext('2d');
    if (g2d) {
      const grad = g2d.createRadialGradient(32, 32, 3, 32, 32, 32);
      grad.addColorStop(0.0, 'rgba(0,0,0,0.5)');
      grad.addColorStop(0.6, 'rgba(0,0,0,0.22)');
      grad.addColorStop(1.0, 'rgba(0,0,0,0)');
      g2d.fillStyle = grad;
      g2d.fillRect(0, 0, 64, 64);
    }
    return new THREE.CanvasTexture(c);
  };
  const shadowTex = makeGroundShadow();
  const shadowMat = new THREE.MeshBasicMaterial({
    map: shadowTex, transparent: true, depthWrite: false, opacity: 0.85,
  });
  const shadowPlane = new THREE.Mesh(new THREE.PlaneGeometry(R * 2.8, R * 2.8), shadowMat);
  shadowPlane.position.set(0, 0, -R * 0.999); // テーブル面（球の真下）・PlaneGeometry は既定で +Z 向き
  scene.add(shadowPlane);

  // シャフトのお尻フェード用アルファ（先端側=不透明 → バット側を急激に透明に）。
  // CylinderGeometry の uv.v は bottom(球側)=0 / top(バット側)=1。flipY 既定で
  // canvas 下端→v=0、上端→v=1。よって「下=不透明 / 上(=お尻)を急に透明」で作る。
  const makeShaftAlpha = (): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = 2;
    c.height = 64;
    const g2d = c.getContext('2d');
    if (g2d) {
      const grad = g2d.createLinearGradient(0, 0, 0, 64);
      grad.addColorStop(0.0, 'rgba(255,255,255,0)'); // 上端=お尻=透明
      grad.addColorStop(0.55, 'rgba(255,255,255,1)'); // バット側だけを「なだらかに」フェード（段差を作らない）
      grad.addColorStop(1.0, 'rgba(255,255,255,1)'); // 下端=先端側=不透明
      g2d.fillStyle = grad;
      g2d.fillRect(0, 0, 2, 64);
    }
    return new THREE.CanvasTexture(c);
  };
  const shaftAlpha = makeShaftAlpha();

  // キュー: 暗茶チップ → 細い白フェルール → 黒シャフト（短く・お尻は急激フェード）。
  // 全体半透明で後ろの球が透ける。
  const cue = new THREE.Group();
  const CUE_OPACITY = 0.85; // 透けすぎないよう不透明寄りに（後ろの球がうっすら見える程度）
  const tipMat = new THREE.MeshStandardMaterial({
    color: 0x241810, roughness: 0.7, transparent: true, opacity: CUE_OPACITY, depthWrite: false,
  });
  const ferruleMat = new THREE.MeshStandardMaterial({
    color: 0xece7da, roughness: 0.45, transparent: true, opacity: CUE_OPACITY, depthWrite: false,
  });
  const shaftMat = new THREE.MeshStandardMaterial({
    color: 0x141414, roughness: 0.45, transparent: true, opacity: CUE_OPACITY,
    depthWrite: false, alphaMap: shaftAlpha,
  });
  const tipR = R * 0.19;
  const tipLen = R * 0.16;
  const ferruleLen = R * 0.05;
  const shaftLen = R * 2.1; // 短く（お尻フェードで自然に消す）
  const tip = new THREE.Mesh(new THREE.CylinderGeometry(tipR, tipR * 0.98, tipLen, 28), tipMat);
  tip.position.y = tipLen / 2;
  const ferrule = new THREE.Mesh(
    new THREE.CylinderGeometry(R * 0.195, tipR, ferruleLen, 28),
    ferruleMat,
  );
  ferrule.position.y = tipLen + ferruleLen / 2;
  const shaft = new THREE.Mesh(
    // openEnded=true: バット側のフタ（cap）を作らない。フタは alphaMap で消えず
    // リング状に残り「末端にまた色がつく」不自然さの原因になるため。
    new THREE.CylinderGeometry(R * 0.24, R * 0.2, shaftLen, 28, 1, true), // 緩いテーパー（手前 0.20→奥 0.24）
    shaftMat,
  );
  shaft.position.y = tipLen + ferruleLen + shaftLen / 2;
  cue.add(tip, ferrule, shaft);
  scene.add(cue);

  let ox = 0.0;
  let oy = 0.0;
  let elevation = Math.PI / 30; // 実仰角（約6°）。撞点モードでは表示は常に最低に固定。
  let mode: 'spin' | 'elev' = 'spin'; // 撞点モード / 仰角モード（状態機械 §1）
  const CUE_STANDOFF = R * 0.02;
  const SPIN_MODE_ELEV = Math.PI / 30; // 撞点モードでキューを寝かせる最低仰角（約6°）
  const SIDE_LEAN = 0.6; // 撞点の左右(ox)に応じてキューを左右へ傾ける量

  const tmpQuat = new THREE.Quaternion();
  const contact = new THREE.Vector3(); // 接触点（球面上・world）
  const horizApproach = new THREE.Vector3(); // キューの水平接近方向（テーブル面内）
  const outDir = new THREE.Vector3(); // キュー接近方向（world）

  // 表示に使う仰角: 撞点モードでは常に最低（寝かせて撞点選択を妨げない）。
  const effectiveElev = (): number => (mode === 'spin' ? SPIN_MODE_ELEV : elevation);

  const layout = (): void => {
    // 撞点 ox,oy は「画面の右(+camRight)・上(+camUp)」方向のオフセット（±0.5）。
    const sx = ox * R;
    const sy = oy * R;
    const depth = Math.sqrt(Math.max(0, R * R - sx * sx - sy * sy));
    // 接触点を手前半球の球面上にカメラ基準で構成＝クリック位置と一致・左右上下対称。
    contact
      .copy(camRight)
      .multiplyScalar(sx)
      .addScaledVector(camUp, sy)
      .addScaledVector(camToward, depth);
    // 水平接近方向: テーブル面内でプレイヤー側(camFlat)を基本に、撞点の左右(ox)へ傾ける。
    // （カメラは斜め上から見るので、水平でも端面=直撃にならず常に視認できる。）
    horizApproach.copy(camFlat).addScaledVector(camFlatRight, ox * SIDE_LEAN).normalize();
    // 仰角 e はテーブル水平面からの起こし角。0=水平に寝る / 大=+Z へ立つ（massé 寄り）。
    const e = effectiveElev();
    outDir
      .copy(horizApproach)
      .multiplyScalar(Math.cos(e))
      .addScaledVector(Z_UP, Math.sin(e))
      .normalize();
    cue.position.copy(contact).addScaledVector(outDir, CUE_STANDOFF);
    cue.quaternion.copy(tmpQuat.setFromUnitVectors(FACE_AXIS, outDir));
  };
  layout();

  const setContactInternal = (nx: number, ny: number): void => {
    let x = nx;
    let y = ny;
    const m = Math.hypot(x, y);
    if (m > 0.5) {
      x = (x / m) * 0.5;
      y = (y / m) * 0.5;
    }
    ox = x;
    oy = y;
    layout();
  };

  // ---- 入力: 画面右下に DOM オーバーレイ。モードで操作を切替（撞点ドラッグ / 仰角縦ドラッグ）----
  const overlay = document.createElement('div');
  overlay.style.cssText =
    'position:fixed;z-index:26;touch-action:none;cursor:grab;background:transparent;';
  document.body.appendChild(overlay);

  // モード切替ボタン（オーバーレイ下部中央）。撞点モード⇄仰角モードを明示切替する。
  const modeBtn = document.createElement('button');
  modeBtn.type = 'button';
  modeBtn.dataset.btn = '1'; // ドラッグ判定から除外するための目印
  modeBtn.style.cssText =
    'position:absolute;left:50%;bottom:6px;transform:translateX(-50%);z-index:1;' +
    'padding:6px 14px;border:none;border-radius:999px;font-size:13px;font-weight:600;' +
    'color:#fff;background:rgba(20,22,28,0.72);cursor:pointer;touch-action:manipulation;' +
    'box-shadow:0 1px 4px rgba(0,0,0,0.35);';
  overlay.appendChild(modeBtn);

  const syncModeBtn = (): void => {
    modeBtn.textContent = mode === 'spin' ? '仰角を調整 ▲' : '← 撞点へ戻る';
  };
  const setMode = (m: 'spin' | 'elev'): void => {
    mode = m;
    syncModeBtn();
    layout();
  };
  syncModeBtn();
  modeBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation(); // ドラッグ開始に巻き込まれないように
  });
  modeBtn.addEventListener('click', () => setMode(mode === 'spin' ? 'elev' : 'spin'));

  // 直近のレイアウト矩形（CSS 座標・左上原点）。render() が毎フレーム更新する。
  const rect = { x: 0, top: 0, size: 0 };
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const facePlane = new THREE.Plane(camToward.clone(), 0); // 原点を通りカメラに正対する平面
  const hitPoint = new THREE.Vector3();
  let dragging = false;
  let lastY = 0;

  const applySpinFromPointer = (clientX: number, clientY: number): void => {
    if (rect.size <= 0) return;
    // クリック光線をボール正対面（原点平面）に当て、カメラの右/上軸で ox,oy を求める。
    // これで「クリックした画面位置」と接触点が一致し、左右・上下が対称になる。
    ndc.x = ((clientX - rect.x) / rect.size) * 2 - 1;
    ndc.y = -(((clientY - rect.top) / rect.size) * 2 - 1);
    raycaster.setFromCamera(ndc, camera);
    if (!raycaster.ray.intersectPlane(facePlane, hitPoint)) return;
    setContactInternal(hitPoint.dot(camRight) / R, hitPoint.dot(camUp) / R);
  };
  const onDown = (e: PointerEvent): void => {
    if ((e.target as HTMLElement | null)?.dataset.btn) return; // ボタン押下はドラッグにしない
    e.preventDefault();
    dragging = true;
    lastY = e.clientY;
    try {
      overlay.setPointerCapture(e.pointerId);
    } catch {
      /* capture 非対応でも以降の move は overlay で受ける */
    }
    // 撞点モード: タップ位置で即撞点確定。仰角モード: 撞点は固定し縦ドラッグで仰角のみ。
    if (mode === 'spin') applySpinFromPointer(e.clientX, e.clientY);
  };
  const onMove = (e: PointerEvent): void => {
    if (!dragging) return;
    e.preventDefault();
    if (mode === 'spin') {
      applySpinFromPointer(e.clientX, e.clientY);
    } else {
      // 上方向ドラッグ＝仰角アップ（キューが立つ）／下方向＝寝かす。
      elevation = clamp(elevation - (e.clientY - lastY) * 0.008, 0, MAX_ELEV);
      lastY = e.clientY;
      layout();
    }
  };
  const onUp = (): void => {
    dragging = false;
  };
  overlay.addEventListener('pointerdown', onDown);
  overlay.addEventListener('pointermove', onMove);
  overlay.addEventListener('pointerup', onUp);
  overlay.addEventListener('pointercancel', onUp);

  return {
    render: (renderer: THREE.WebGLRenderer, cssW: number, cssH: number): void => {
      try {
        // 画面右下（既存撞点UIの領域）にアンカー。キューの移動／引きスペースを含む大きさ。
        const size = Math.round(Math.min(cssW, cssH) * 0.5);
        if (size <= 0) return;
        const margin = 10;
        const x = Math.round(cssW - size - margin);
        const yBottom = margin; // three.js ビューポートは左下原点
        // DOM オーバーレイ（左上原点）を同じ矩形へ追従。
        const top = cssH - size - margin;
        if (rect.x !== x || rect.top !== top || rect.size !== size) {
          rect.x = x;
          rect.top = top;
          rect.size = size;
          overlay.style.left = `${x}px`;
          overlay.style.top = `${top}px`;
          overlay.style.width = `${size}px`;
          overlay.style.height = `${size}px`;
        }
        renderer.setScissorTest(true);
        renderer.setViewport(x, yBottom, size, size);
        renderer.setScissor(x, yBottom, size, size);
        renderer.autoClear = false; // 色はクリアしない（盤面を残す＝背景透明）
        renderer.clearDepth();
        renderer.render(scene, camera);
      } catch (err) {
        console.error('cue-spin-widget 描画に失敗:', err);
      } finally {
        renderer.autoClear = true;
        renderer.setScissorTest(false);
        renderer.setViewport(0, 0, cssW, cssH);
      }
    },
    setContact: (nx: number, ny: number): void => setContactInternal(nx, ny),
    setElevation: (rad: number): void => {
      elevation = clamp(rad, 0, MAX_ELEV);
      layout();
    },
    dispose: (): void => {
      overlay.removeEventListener('pointerdown', onDown);
      overlay.removeEventListener('pointermove', onMove);
      overlay.removeEventListener('pointerup', onUp);
      overlay.removeEventListener('pointercancel', onUp);
      overlay.remove();
      shaftAlpha.dispose();
      shadowTex.dispose();
      dotGeo.dispose(); // 6 個の練習ドットで共有しているジオメトリ/マテリアル
      dotMat.dispose();
      for (const m of [ball, shadowPlane, tip, ferrule, shaft]) {
        m.geometry.dispose();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else mat.dispose();
      }
    },
  };
};
